import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MdDelete,
  MdEdit,
  MdAdd,
  MdSearch,
  MdAccessTime,
  MdWbSunny,
  MdNightsStay,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdSchedule,
} from "react-icons/md";
import toast from "react-hot-toast";
import {
  getAllTimeslots,
  createTimeslot,
  updateTimeslot,
  deleteTimeslot,
} from "../apis/timeslotApi";
import { brand } from "../theme";

const formatTimeToIST = (timeString) => {
  if (!timeString) return "—";
  const [hours, minutes] = timeString.split(":");
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};

const getShiftInfo = (timeString) => {
  if (!timeString) return { label: "General", color: brand.slate };
  const [hours] = timeString.split(":").map(Number);

  if (hours < 12) {
    return { label: "Morning Shift", bg: "#FFEAD2", color: "#B45309", icon: <MdWbSunny size={14} /> };
  } else if (hours < 17) {
    return { label: "Afternoon Shift", bg: "#E0F2FE", color: "#0369A1", icon: <MdWbSunny size={14} /> };
  }
  return { label: "Evening Shift", bg: "#EDE9FE", color: "#6D28D9", icon: <MdNightsStay size={14} /> };
};

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Timeslots = () => {
  const [timeslots, setTimeslots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [shiftFilter, setShiftFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ start_time: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const loadTimeslots = async () => {
    setLoading(true);
    try {
      const res = await getAllTimeslots();
      setTimeslots(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.error("Failed to fetch timeslots");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeslots();
  }, []);

  const stats = useMemo(() => {
    let morning = 0;
    let afternoon = 0;
    let evening = 0;

    timeslots.forEach((slot) => {
      if (!slot.start_time) return;
      const [h] = slot.start_time.split(":").map(Number);
      if (h < 12) morning += 1;
      else if (h < 17) afternoon += 1;
      else evening += 1;
    });

    return {
      total: timeslots.length,
      morning,
      afternoon,
      evening,
    };
  }, [timeslots]);

  const filteredTimeslots = useMemo(() => {
    return timeslots.filter((slot) => {
      if (!slot.start_time) return true;
      const [h] = slot.start_time.split(":").map(Number);

      if (shiftFilter === "morning" && h >= 12) return false;
      if (shiftFilter === "afternoon" && (h < 12 || h >= 17)) return false;
      if (shiftFilter === "evening" && h < 17) return false;

      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const formatted = formatTimeToIST(slot.start_time).toLowerCase();
      const raw = slot.start_time.toLowerCase();

      return formatted.includes(q) || raw.includes(q);
    });
  }, [timeslots, shiftFilter, search]);

  const paginatedTimeslots = useMemo(() => {
    return filteredTimeslots.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTimeslots, page, rowsPerPage]);

  /* ------------------ HANDLERS ------------------ */

  const handleOpen = (slot = null) => {
    if (slot) {
      setForm({ start_time: slot.start_time });
      setEditing(true);
      setSelectedId(slot.id);
    } else {
      setForm({ start_time: "" });
      setEditing(false);
      setSelectedId(null);
    }
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setForm({ start_time: "" });
    setSelectedId(null);
    setEditing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.start_time) {
      toast.error("Please specify a start time");
      return;
    }

    setSaving(true);
    try {
      if (editing && selectedId) {
        await updateTimeslot(selectedId, form);
        toast.success("Timeslot updated successfully");
      } else {
        await createTimeslot(form);
        toast.success("Timeslot created successfully");
      }
      handleClose();
      loadTimeslots();
    } catch (error) {
      toast.error("Error saving timeslot");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTimeslot(confirmDelete.id);
      toast.success("Timeslot deleted successfully");
      loadTimeslots();
    } catch {
      toast.error("Failed to delete timeslot");
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Timeslots Operations
            </Typography>
            <Chip
              label={`${timeslots.length} Active Slots`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage appointment dispatch timeslots, shift classifications, and booking windows
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload timeslot schedule">
            <IconButton
              onClick={loadTimeslots}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => handleOpen()}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Add Timeslot
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdAccessTime size={24} />} label="Total Timeslots" count={stats.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdWbSunny size={24} />} label="Morning Slots" count={stats.morning} accent="#F59E0B" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdWbSunny size={24} />}
            label="Afternoon Slots"
            count={stats.afternoon}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdNightsStay size={24} />}
            label="Evening Slots"
            count={stats.evening}
            accent="#8B5CF6"
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search by time e.g. 09:00 AM..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", md: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Shift Filter Chips & View Toggle */}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box display="flex" gap={1} flexWrap="wrap">
              {[
                { label: `All (${timeslots.length})`, val: "all" },
                { label: `Morning (${stats.morning})`, val: "morning" },
                { label: `Afternoon (${stats.afternoon})`, val: "afternoon" },
                { label: `Evening (${stats.evening})`, val: "evening" },
              ].map((shift) => (
                <Chip
                  key={shift.val}
                  label={shift.label}
                  onClick={() => {
                    setShiftFilter(shift.val);
                    setPage(0);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    px: 0.5,
                    bgcolor: shiftFilter === shift.val ? brand.ink950 : `${brand.slateLight}25`,
                    color: shiftFilter === shift.val ? brand.parchment : brand.slate,
                    "&:hover": { bgcolor: shiftFilter === shift.val ? brand.ink800 : `${brand.slateLight}40` },
                  }}
                />
              ))}
            </Box>

            {/* View Switcher */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                bgcolor: `${brand.slateLight}20`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.paper,
                    color: brand.ink950,
                    boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <MdViewList size={18} />
              </ToggleButton>
              <ToggleButton value="grid">
                <MdGridView size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Start Time (IST)</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Shift Category</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Raw 24H Time</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Created At</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedTimeslots.length > 0 ? (
                paginatedTimeslots.map((slot, index) => {
                  const shift = getShiftInfo(slot.start_time);
                  return (
                    <TableRow
                      key={slot.id || index}
                      sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                    >
                      <TableCell
                        sx={{
                          color: brand.slate,
                          fontFamily: '"IBM Plex Mono", monospace',
                          fontSize: "0.82rem",
                          fontWeight: 600,
                        }}
                      >
                        {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <MdSchedule size={18} color={brand.brass} />
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.98rem",
                              color: brand.ink950,
                              fontFamily: '"IBM Plex Mono", monospace',
                            }}
                          >
                            {formatTimeToIST(slot.start_time)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={shift.icon}
                          label={shift.label}
                          size="small"
                          sx={{
                            bgcolor: shift.bg,
                            color: shift.color,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                        {slot.start_time || "—"}
                      </TableCell>
                      <TableCell sx={{ color: brand.slate, fontSize: "0.82rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                        {slot.created_at || "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={0.8} justifyContent="flex-end">
                          <MuiTooltip title="Edit Timeslot">
                            <IconButton
                              size="small"
                              onClick={() => handleOpen(slot)}
                              sx={{ bgcolor: `${brand.slateLight}25`, color: brand.ink950 }}
                            >
                              <MdEdit size={16} />
                            </IconButton>
                          </MuiTooltip>
                          <MuiTooltip title="Delete Timeslot">
                            <IconButton
                              size="small"
                              onClick={() => setConfirmDelete({ open: true, id: slot.id })}
                              sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                            >
                              <MdDelete size={16} />
                            </IconButton>
                          </MuiTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <MdAccessTime size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No timeslots found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search || shiftFilter !== "all"
                        ? "Try clearing your search query or shift filter."
                        : "Click 'Add Timeslot' to configure your first booking slot."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredTimeslots.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedTimeslots.map((slot) => {
            const shift = getShiftInfo(slot.start_time);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={slot.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    bgcolor: brand.paper,
                    border: `1px solid ${brand.slateLight}40`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                      borderColor: `${brand.brass}66`,
                    },
                  }}
                >
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: brand.ink950,
                          color: brand.brassLight,
                        }}
                      >
                        <MdAccessTime size={24} />
                      </Avatar>
                      <Chip
                        icon={shift.icon}
                        label={shift.label}
                        size="small"
                        sx={{
                          bgcolor: shift.bg,
                          color: shift.color,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          "& .MuiChip-icon": { color: "inherit" },
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontWeight: 700,
                        fontSize: "1.4rem",
                        color: brand.ink950,
                      }}
                    >
                      {formatTimeToIST(slot.start_time)}
                    </Typography>

                    <Typography sx={{ fontSize: "0.78rem", color: brand.slate, mt: 0.5, fontFamily: '"IBM Plex Mono", monospace' }}>
                      24H Format: {slot.start_time || "—"}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1} justifyContent="flex-end" mt={2.5} pt={2} borderTop={`1px solid ${brand.slateLight}30`}>
                    <Button size="small" variant="outlined" onClick={() => handleOpen(slot)} startIcon={<MdEdit />}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setConfirmDelete({ open: true, id: slot.id })}
                      startIcon={<MdDelete />}
                    >
                      Delete
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Dialog: Add / Edit Timeslot ────────────────────────────────── */}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 440 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          {editing ? "Edit Dispatch Timeslot" : "Create New Timeslot"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              type="time"
              label="Start Time *"
              name="start_time"
              value={form.start_time}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            {form.start_time && (
              <Box bgcolor={`${brand.brassSoft}88`} p={2} borderRadius="12px" textAlign="center">
                <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>Formatted Preview (IST)</Typography>
                <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, fontSize: "1.2rem", color: brand.ink950, mt: 0.5 }}>
                  {formatTimeToIST(form.start_time)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: "10px", px: 3 }}
          >
            {saving ? "Saving..." : editing ? "Update Slot" : "Create Slot"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirm Delete ─────────────────────────────────────── */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, id: null })}
        PaperProps={{ sx: { borderRadius: "20px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700 }}>
          Delete Timeslot?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.9rem" }}>
            Are you sure you want to delete this timeslot? Active appointment schedules relying on this slot may be affected.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete({ open: false, id: null })} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ fontWeight: 700 }}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Timeslots;
